import CreateIcon from '@mui/icons-material/Create';
import { format } from 'date-fns';
import { Td, Th, Tr } from '@/trio-ui/Table';
import { Team } from '@/types/api';
import { JobInfo, StaffMember } from '@/types/api/staffMember';
import { makeDateWithCurrentTime } from '@/utils/dates';
import * as Styles from './StaffMemberJobSection.styles';
import HeaderTabSelector from '../HeaderTabSelector';
import IconButton from '../IconButton';
import SectionBox from '../SectionBox';
import StaffMemberJobItem from '../StaffMemberJobItem';

export type Props = {
  id?: string;
  tab: Tab;
  directLeader?: StaffMember;
  onChangeTab?: (tab: Tab) => void;
  teams?: Team[];
  staffMemberId: string;
  jobInfos?: JobInfo[];
  onAddJobInfoClick?: () => void;
  addingJobInfo?: boolean;
  onAddCancel?: () => void;
  editing?: Record<string, boolean>;
  onEditClick?: (id: string) => void;
  onEditCancel?: (id: string) => void;
  onAddNewJob?: () => void;
  onUpdateJob?: (id: string) => void;
  onDeleteJob?: (id: string) => void;
  onUpdateReporters?: () => void;
};

export const NEW_JOB_FORM_ID = 'staff-member-job-new-form';

export type Tab = 'Summary' | 'Details';
const tabs: Tab[] = ['Summary', 'Details'];

const DirectLeaderName = ({ directLeader }: { directLeader?: StaffMember }): JSX.Element => {
  if (!directLeader?.person) {
    return <span />;
  }

  return (
    <span>
      {directLeader.person.firstName} {directLeader.person.lastName}
    </span>
  );
};

const StaffMemberJobSection = ({
  id,
  tab,
  directLeader,
  onChangeTab,
  staffMemberId,
  teams = [],
  jobInfos = [],
  onAddJobInfoClick,
  addingJobInfo = false,
  onAddCancel,
  editing = {},
  onEditCancel,
  onEditClick,
  onAddNewJob,
  onUpdateJob,
  onDeleteJob,
  onUpdateReporters,
}: Props): JSX.Element => {
  return (
    <SectionBox title="Job" id={id} className="tw-relative">
      <HeaderTabSelector
        className="tw-absolute tw-top-8 tw-right-8"
        options={tabs}
        value={tab}
        onChange={onChangeTab}
      />
      {tab === 'Summary' ? (
        <Styles.TableContainer>
          <Styles.Table
            customBorderRadius={8}
            data={jobInfos}
            renderHeader={
              <Tr>
                <Th>Effective Date</Th>
                <Th>Seniority</Th>
                <Th>Job Title</Th>
                <Th>Direct leader</Th>
                <Th>Direct reports</Th>
                <Th />
              </Tr>
            }
            renderEmptyState={
              <Tr>
                <Td
                  colSpan={6}
                  textAlign="center"
                  padding="28px 74px"
                  fontSize={14}
                  lineHeight="150%"
                >
                  No jobs.
                </Td>
              </Tr>
            }
            renderRow={(data) => {
              return (
                <Tr key={data.id}>
                  <Td>{format(makeDateWithCurrentTime(data.fromDate), 'MM/dd/yyyy')}</Td>
                  <Td>{data.seniority}</Td>
                  <Td>{data.title}</Td>
                  <Td>
                    <DirectLeaderName directLeader={directLeader} />
                  </Td>
                  <Td>#</Td>
                  <Td>
                    <IconButton onClick={() => onEditClick?.(data.id)} icon={CreateIcon} />
                  </Td>
                </Tr>
              );
            }}
          />
        </Styles.TableContainer>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-6">
          {jobInfos.map((jobInfo) => (
            <StaffMemberJobItem
              key={jobInfo.id}
              jobInfo={jobInfo}
              teamsList={teams}
              staffMemberId={staffMemberId}
              isEditing={editing[jobInfo.id]}
              onEditCancel={() => onEditCancel?.(jobInfo.id)}
              onEditClick={() => onEditClick?.(jobInfo.id)}
              onUpdateJob={() => onUpdateJob?.(jobInfo.id)}
              onDeleteJob={() => onDeleteJob?.(jobInfo.id)}
              onRemoveDirectReport={onUpdateReporters}
              onAddDirectReport={onUpdateReporters}
            />
          ))}
        </div>
      )}
      <div className="tw-mt-6">
        {addingJobInfo && (
          <StaffMemberJobItem
            id={NEW_JOB_FORM_ID}
            teamsList={teams}
            staffMemberId={staffMemberId}
            onAddCancel={onAddCancel}
            onAddNewJob={onAddNewJob}
          />
        )}
        {!addingJobInfo && (
          <button
            type="button"
            className="tw-self-start tw-text-primary-60 tw-text-sm"
            onClick={onAddJobInfoClick}
          >
            + Job
          </button>
        )}
      </div>
    </SectionBox>
  );
};

export default StaffMemberJobSection;
